/**
 * Modal for assigning a competency to one or more team members.
 * Fetches the full team on open; POSTs to /api/competencies/assign.
 */

import { useEffect, useMemo, useState } from "react";
import { X, Search, Users, Check } from "lucide-react";
import { api } from "../api";

export default function AssignCompetencyModal({
  competency,
  isDemo,
  onClose,
  onAssigned,
}) {
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [existingByMember, setExistingByMember] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/team"),
      api.get(`/competencies/assignments?competency_id=${encodeURIComponent(competency.id)}`),
    ])
      .then(([t, a]) => {
        if (!alive) return;
        setTeam(t);
        const map = {};
        for (const r of a) {
          if (r.status !== "completed") map[r.team_member_id] = r;
        }
        setExistingByMember(map);
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [competency.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return team;
    return team.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.role || "").toLowerCase().includes(q)
    );
  }, [team, query]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/competencies/assign", {
        competency_id: competency.id,
        competency_name: competency.name,
        category_id: competency.category_id,
        team_member_ids: Array.from(selected),
        version: competency.version || "1.0",
        pass_mark: competency.pass_mark || 80,
      });
      onAssigned?.(res);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const eligible = filtered.filter(
    (m) => !existingByMember[m.id]
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="assign-competency-modal"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
              Assign competency
            </div>
            <h2 className="mt-1 font-display text-lg font-semibold text-slate-900 leading-tight">
              {competency.name}
            </h2>
            <div className="mt-1 text-[12px] text-slate-500">
              v{competency.version || "1.0"} · Pass mark{" "}
              {competency.pass_mark || 80}%
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="assign-competency-close"
            className="text-slate-400 hover:text-slate-700 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <label className="relative block">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team by name or role…"
              data-testid="assign-competency-search"
              className="w-full pl-9 pr-3 py-2.5 text-[13px] rounded-lg border border-slate-200 bg-slate-50/70 focus:bg-white focus:border-[#D4A65A]/60 focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none text-slate-700 placeholder:text-slate-400"
            />
          </label>
        </div>

        {/* Team list */}
        <div className="px-3 pt-3 pb-1 max-h-72 overflow-y-auto">
          {loading && (
            <div className="text-center text-[12.5px] text-slate-500 py-6">
              Loading team…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center text-[12.5px] text-slate-500 py-6">
              No matching team members.
            </div>
          )}
          <ul>
            {filtered.map((m) => {
              const already = existingByMember[m.id];
              const checked = selected.has(m.id);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    disabled={!!already}
                    onClick={() => toggle(m.id)}
                    data-testid={`assign-competency-member-${m.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      already
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-[#0B1220] border-[#0B1220] text-white"
                          : "bg-white border-slate-300"
                      }`}
                    >
                      {checked && <Check size={12} />}
                    </span>
                    <span className="h-8 w-8 rounded-full bg-[#0B1220] text-[#D4A65A] flex items-center justify-center text-[11px] font-semibold shrink-0">
                      {initials(m.name)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13.5px] text-slate-900 font-semibold truncate">
                        {m.name}
                      </span>
                      <span className="block text-[11.5px] text-slate-500 truncate">
                        {m.role || "Team member"}
                      </span>
                    </span>
                    {already && (
                      <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 shrink-0">
                        Already assigned
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-[12px] text-slate-500 inline-flex items-center gap-1.5">
            <Users size={12} />
            {selected.size} selected · {eligible.length} eligible
          </div>
          <div className="flex items-center gap-2">
            {isDemo && (
              <span className="text-[10.5px] text-[#8C6B33]">
                Disabled in demo mode
              </span>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={busy || isDemo || selected.size === 0}
              data-testid="assign-competency-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 disabled:cursor-not-allowed text-[#0B1220] px-4 py-2 text-[13px] font-semibold"
            >
              {busy ? "Assigning…" : `Assign to ${selected.size || 0}`}
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}
